import { DestroyRef, Injectable, inject } from '@angular/core'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet'
import { LogService } from './log.service'
import { AdminConfirmDialogComponent } from 'src/app/admin/dialog/confirm/admin-confirm-dialog.component'
import { ConfirmDialogComponent } from 'src/app/components/dialog/confirm/confirm-dialog.component'
import { AdminInputDialogComponent } from 'src/app/admin/dialog/input/admin-input-dialog.component'
import { EventService } from './event.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MediaLibraryDialogComponent } from 'src/app/components/dialog/media-library/media-library.component'
import { AdminI18nEntryDialogComponent } from 'src/app/admin/dialog/i18n-entry/admin-i18n-entry-dialog.component'
import { AdminProcessingDialogComponent } from 'src/app/admin/dialog/processing/admin-processing-dialog.component'
import { IAdminProcessingDialogOptions, IComparisonModalOptions, IConfirmDialogOptions, IContactDialogOptions, IGalleryDialogOptions, II18nEntryDialogOptions, IIframeDialogOptions, IInputDialogOptions, IMediaLibraryDialogOptions } from '../interfaces/modal.interface'
import { ClientService } from './client.service'
import { GalleryDialogComponent } from 'src/app/components/dialog/gallery/gallery-dialog.component'
import { GlobalDataService } from './global-data.service'
import { IframeDialogComponent } from 'src/app/admin/dialog/iframe/iframe-dialog.component'


@Injectable({
  providedIn: 'root',
})
export class ModalService {
  dialog = inject(MatDialog)
  bottomSheet = inject(MatBottomSheet)
  logger = inject(LogService)
  private _eventService = inject(EventService)
  private _globalData = inject(GlobalDataService)
  private _clientService = inject(ClientService)
  private _destroyRef = inject(DestroyRef)

  init(): void {
    this._eventService.contactClicked$.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(asset => {
      const contactFormConfig = this._globalData.config().contactForm
      const assetCard = contactFormConfig?.data?.components?.assetCard
      const badgesContainer = assetCard?.data?.components?.badgesContainer
      // No need to await since we don't need the return value here
      this.contact({
        title: asset ? $localize`:@@contact-form.dialog.title-for-asset-contact:Contact Sales` : $localize`:@@contact-form.dialog.title-for-no-asset-contact:Contact Us`,
        asset,
        successTitle: contactFormConfig?.data?.successTitle,
        successBody: contactFormConfig?.data?.successBody,
        badgesConfig: badgesContainer,
        channelWebshopSellingContactPhone: this._clientService.client().channel_webshop_selling_contact_phone?.trim()
      })
    })
  }

  private _confirm(
    confirmOptions: IConfirmDialogOptions,
    dialogComponent: typeof AdminConfirmDialogComponent | typeof ConfirmDialogComponent,
  ): MatDialogRef<AdminConfirmDialogComponent | ConfirmDialogComponent, any> {
    const {
      title = null,
      content = null,
      cancelLabel = 'Cancel',
      confirmLabel = 'Confirm',
    } = confirmOptions
    return this.dialog.open(dialogComponent, {
      data: {
        title,
        content,
        cancelLabel,
        confirmLabel,
      },
    })
  }

  confirm(confirmOptions: IConfirmDialogOptions): MatDialogRef<ConfirmDialogComponent, any> {
    return this._confirm(confirmOptions, ConfirmDialogComponent)
  }

  confirmAdmin(confirmOptions: IConfirmDialogOptions): MatDialogRef<AdminConfirmDialogComponent, any> {
    return this._confirm(confirmOptions, AdminConfirmDialogComponent)
  }

  private _mediaLibrary(
    mediaLibraryOptions: IMediaLibraryDialogOptions,
  ): MatDialogRef<MediaLibraryDialogComponent, any> {
    const {
      type = '',
      title = 'Media Library',
      panelClass = 'media-library-dialog',
      mode = 'select',
      onSelect = () => {},
    } = mediaLibraryOptions
    this.dialog.closeAll()
    return this.dialog.open(MediaLibraryDialogComponent, {
      data: {
        type,
        title,
        mode,
        onSelect: onSelect,
      },
      panelClass,
      autoFocus: 'input',
    })
  }

  mediaLibrary(mediaLibraryOptions: IMediaLibraryDialogOptions): MatDialogRef<MediaLibraryDialogComponent, any> {
    return this._mediaLibrary(mediaLibraryOptions)
  }



  private async _comparison(
    comparisonOptions: IComparisonModalOptions,
  ): Promise<MatBottomSheetRef<any, any>> {
    const {
      panelClass = 'comparison-modal',
      onSelect = () => {},
    } = comparisonOptions
    this.bottomSheet.dismiss()

    // Dynamic import to avoid circular dependency
    const { ComparisonModalComponent } = await import('src/app/components/comparison-modal/comparison-modal.component')

    return this.bottomSheet.open(ComparisonModalComponent, {
      data: {
        onSelect,
      },
      panelClass: [panelClass, 'theme-bottom-sheet'],
      autoFocus: 'input',
      height: '100%'
    })
  }

  async comparison(comparisonOptions: IComparisonModalOptions): Promise<MatBottomSheetRef<any, any>> {
    return this._comparison(comparisonOptions)
  }

  private _input(
    inputOptions: IInputDialogOptions,
    dialogComponent: typeof AdminInputDialogComponent,
  ): MatDialogRef<AdminInputDialogComponent, any> {
    const {
      title = null,
      inputs = [],
      cancelLabel = 'Cancel',
      confirmLabel = 'Submit',
      validate = () => true,
    } = inputOptions
    return this.dialog.open(dialogComponent, {
      data: {
        title,
        inputs,
        cancelLabel,
        confirmLabel,
        validate,
      }
    })
  }

  inputAdmin(inputOptions: IInputDialogOptions): MatDialogRef<AdminInputDialogComponent, any> {
    return this._input(inputOptions, AdminInputDialogComponent)
  }

  async contact(
    contactOptions: IContactDialogOptions,
  ): Promise<MatDialogRef<any, any>> {
    const {
      title = $localize`:@@contact-form.dialog.title-for-no-asset-contact:Contact Us`,
      asset = null,
      successTitle = 'Success',
      successBody = '',
      badgesConfig = null,
      channelWebshopSellingContactPhone = ''
    } = contactOptions

    // Dynamic import to avoid circular dependency
    const { ContactDialogComponent } = await import('src/app/components/dialog/contact/contact-dialog.component')

    return this.dialog.open(ContactDialogComponent, {
      data: {
        title,
        asset,
        successTitle,
        successBody,
        badgesConfig,
        channelWebshopSellingContactPhone,
      },
      panelClass: ['contact-dialog', 'theme-dialog'],
      autoFocus: 'input',
    })
  }

  i18nEntry(options: II18nEntryDialogOptions): MatDialogRef<AdminI18nEntryDialogComponent, any> {
    const {
      label,
      key,
      config,
      type = 'input',
      placeholder = '',
      panelClass = 'i18n-entry-dialog',
      preview,
      maxLength,
    } = options
    this.dialog.closeAll()
    return this.dialog.open(AdminI18nEntryDialogComponent, {
      data: {
        label,
        key,
        config,
        type,
        placeholder,
        preview,
        maxLength,
      },
      panelClass,
      autoFocus: 'input',
      disableClose: true,
    })
  }

  gallery(options: IGalleryDialogOptions): MatDialogRef<GalleryDialogComponent, any> {
    const {
      title,
      images = [],
    } = options
    this.dialog.closeAll()
    return this.dialog.open(GalleryDialogComponent, {
      data: {
        title,
        images,
      },
      panelClass: 'gallery-dialog',
      autoFocus: 'input',
      disableClose: true,
    })
  }

  adminProcessing(options: IAdminProcessingDialogOptions): MatDialogRef<AdminProcessingDialogComponent, any> {
    const {
      content = 'Please wait...',
      panelClass = 'admin-processing-dialog',
    } = options
    this.dialog.closeAll()
    return this.dialog.open(AdminProcessingDialogComponent, {
      data: {
        content,
      },
      panelClass,
      disableClose: true,
    })
  }

  iframe(options: IIframeDialogOptions): MatDialogRef<IframeDialogComponent, any> {
    const {
      src,
    } = options
    this.dialog.closeAll()
    return this.dialog.open(IframeDialogComponent, {
      data: {
        src,
      },
      autoFocus: false,
      height: '90%',
      width: '90%',
      panelClass: ['iframe-dialog', 'theme-dialog'],
      disableClose: true,
    })
  }
}
